import { IS_VC_UNIT_CARD_DETAILS_VISIBLE, VALUE_CHAIN_MAP } from '../constants/general';
import {
  BUSINESS_UNIT_TYPES,
  VALUE_CHAIN_MAP_CUSTOM_NODES,
  VALUE_CHAIN_UNIT_TYPES,
} from '../constants/interfaces';

import type { Node, OnNodeDrag, XYPosition } from '@xyflow/react';
import type { TBusinessUnitType, TValueChainUnitType } from '../constants/interfaces';
import type { IValueChainUnit } from '../store/slices/valueChainMap/interfaces/IValueChainUnit';

export const getUnitHeight = (
  type: TValueChainUnitType,
  businessUnitType?: TBusinessUnitType,
  estimated?: boolean,
): number => {
  let result = 0;
  if (businessUnitType === BUSINESS_UNIT_TYPES.LOGICAL || estimated) {
    result = VALUE_CHAIN_MAP.LOGICAL_STATUS_HEIGHT;
  }

  // While card details are hidden, every unit type renders header-only
  if (!IS_VC_UNIT_CARD_DETAILS_VISIBLE) {
    return result + VALUE_CHAIN_MAP.UNIT_HEADER_HEIGHT;
  }

  switch (type) {
    case VALUE_CHAIN_UNIT_TYPES.PRODUCT:
    case VALUE_CHAIN_UNIT_TYPES.OUTPUT_PRODUCT:
      result += VALUE_CHAIN_MAP.UNIT_INPUT_PRODUCT_HEIGHT;
      break;
    case VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET:
      result += VALUE_CHAIN_MAP.UNIT_OWN_OPERATION_HEIGHT;
      break;
    case VALUE_CHAIN_UNIT_TYPES.PROFIT_CENTER:
      result += VALUE_CHAIN_MAP.UNIT_PROFIT_CENTER_HEIGHT;
      break;
    case VALUE_CHAIN_UNIT_TYPES.CUSTOMER:
      result += VALUE_CHAIN_MAP.UNIT_CUSTOMER_HEIGHT;
      break;
    case VALUE_CHAIN_UNIT_TYPES.SUPPLIER:
    default:
      result += VALUE_CHAIN_MAP.UNIT_SUPPLIER_HEIGHT;
  }
  return result;
};

export const getUnitBusinessUnitType = (
  unit: Pick<IValueChainUnit, 'businessUnitType' | 'records'>,
): TBusinessUnitType | undefined => unit.businessUnitType ?? unit.records?.[0]?.businessUnitType;

export const getUnitEstimated = (
  unit: Pick<IValueChainUnit, 'estimated' | 'records'>,
): boolean | undefined => unit.estimated ?? unit.records?.[0]?.estimated;

export const getUnitDisplayName = (
  unit: Pick<IValueChainUnit, 'name' | 'records'>,
): string => unit.name || unit.records?.[0]?.name || '';

/** The event React Flow hands a node drag handler: the native mouse or touch event of the pointer. */
export type TNodeDragEvent = Parameters<OnNodeDrag>[0];

/** Where the pointer is on the screen during a node drag: the mouse, or the first touch. */
export const readDragPointer = (event: TNodeDragEvent): XYPosition => {
  if ('touches' in event) {
    const touch = event.touches[0] ?? event.changedTouches[0];

    return { x: touch?.clientX ?? 0, y: touch?.clientY ?? 0 };
  }

  return { x: event.clientX, y: event.clientY };
};

export const getTargetedBoard = (dragPosition: { x: number, y: number }, nodes: Node[]) => {
  const boards = nodes.filter((n) => n.type === VALUE_CHAIN_MAP_CUSTOM_NODES.BOARD);
  return boards.find((board) => (
    dragPosition.x > board.position.x
    && dragPosition.x < Number(board.measured?.width) + board.position.x
    && dragPosition.y > board.position.y
    && dragPosition.y < Number(board.measured?.height) + board.position.y
  ));
};

export const getTargetedSwimlane = (dragPosition: { x: number, y: number }, nodes: Node[]) => {
  const swimlanes = nodes.filter((n) => n.type === VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE);
  return swimlanes.find((swimlane) => {
    const actualWidth = swimlane.measured?.width || swimlane.width || 262;
    const actualHeight = swimlane.measured?.height || swimlane.height || 800;
    return (
      dragPosition.x > swimlane.position.x
      && dragPosition.x < swimlane.position.x + actualWidth
      && dragPosition.y > swimlane.position.y
      && dragPosition.y < swimlane.position.y + actualHeight
    );
  });
};

export const isTargetNodeMatching = (closeNode: Node, node: Node): boolean => {
  // Check if target is a unit
  const closeNodeData = closeNode.data as unknown as Pick<IValueChainUnit, 'businessUnitType' | 'records' | 'type'>;
  const nodeData = node.data as unknown as Pick<IValueChainUnit, 'businessUnitType' | 'records' | 'type'>;
  const isTargetUnit = closeNodeData.type === nodeData.type
      && getUnitBusinessUnitType(closeNodeData) === BUSINESS_UNIT_TYPES.LOGICAL
      && getUnitBusinessUnitType(nodeData) === BUSINESS_UNIT_TYPES.PHYSICAL
      // unit shouldn't sit already in this unit
      && closeNode.id !== node.parentId;

  // Check if target is a swimlane
  const isTargetSwimlane = closeNode.type === VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE
    // Allow moving to swimlane if:
    // 1. Card is nested (parentId is a unit) - allow extracting from parent
    // 2. Card is in different swimlane
    && (node.parentId?.startsWith('unit_') || closeNode.id !== node.data.swimlaneId);

  return isTargetUnit || isTargetSwimlane;
};
